import { useMutation, useQuery } from "@tanstack/react-query";
import { APIErrorResponse } from "src/types/generic";
import apiClient from "../apiClient";

interface IParams {
  page: number;
  invoice_status?: string;
  zatca_status?: string;
  type?: "invoice" | "credit_note";
}

export const useGetSalesInvoices = (params: IParams) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-sales-invoices-list", params.page, params.type],
    queryFn: async () => {
      return apiClient.get(`/api/saleInvoices`, { params });
    },
  });

export const useGetInvoiceById = ({
  id,
  enabled,
}: {
  id: string;
  enabled: boolean;
}) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-invoice-by-id", id],
    queryFn: async () => {
      return apiClient.get(`/api/saleInvoices/${id}`);
    },
    gcTime: 0,
    enabled,
  });

export const useAddInvoiceMutation = () =>
  useMutation<any, APIErrorResponse, FormData>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/saleInvoices`, data);
    },
  });

export const useUpdateInvoiceMutation = () =>
  useMutation<void, APIErrorResponse, any>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/saleInvoices/${id}`, rest);
    },
  });

export const useDeleteInvoiceMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/saleInvoices/${id}`);
    },
  });

export const useReturnInvoiceMutation = () =>
  useMutation<any, APIErrorResponse, { id: string }>({
    mutationFn: async (data) => {
      const { id } = data;
      return apiClient.post(
        `/api/saleInvoices/${id}/cancel-and-create-credit-note`,
      );
    },
  });

export const useResubmitInvoiceMutation = () =>
  useMutation<any, APIErrorResponse, { id: string }>({
    mutationFn: async (data) => {
      const { id } = data;
      return apiClient.post(`/api/saleInvoices/${id}/resubmit`);
    },
  });

export const useCancelInvoiceMutation = () =>
  useMutation<void, APIErrorResponse, { id: string | number }>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/saleInvoices/${data.id}/cancel`, data);
    },
  });

// payment
export const useAddInvoicePaymentMutation = () =>
  useMutation<void, APIErrorResponse, FormData>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/saleInvoicePayments`, data);
    },
  });

export const useAddInvoiceClientBalancePaymentMutation = () =>
  useMutation<void, APIErrorResponse, FormData>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/saleInvoiceBalancePayments`, data);
    },
  });

export const useDeleteInvoicePaymentMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/saleInvoicePayments/${id}`);
    },
  });

interface ITerm {
  id?: string;
  title: string;
  is_mandatory?: boolean;
}

export const useGetTermsList = (params?: { is_mandatory?: boolean }) =>
  useQuery<any, APIErrorResponse, { data: ITerm[] }>({
    queryKey: ["get-terms-list", params?.is_mandatory],
    queryFn: async () => {
      return apiClient.get(`/api/terms`, { params });
    },
  });

export const useAddTermMutation = () =>
  useMutation<void, APIErrorResponse, ITerm>({
    mutationFn: async (data) => {
      return apiClient.post(`/api/terms`, data);
    },
  });

export const useUpdateTermMutation = () =>
  useMutation<void, APIErrorResponse, ITerm>({
    mutationFn: async (data) => {
      const { id, ...rest } = data;
      return apiClient.put(`/api/terms/${id}`, rest);
    },
  });

export const useDeleteTermMutation = () =>
  useMutation<void, APIErrorResponse, number>({
    mutationFn: async (id) => {
      return apiClient.delete(`/api/terms/${id}`);
    },
  });

export const useGetClientUnpaidInvoices = (id: number | undefined) =>
  useQuery<any, APIErrorResponse, any>({
    queryKey: ["get-client-unpaid-invoices", id],
    queryFn: async () => {
      return apiClient.get(
        `/api/saleInvoices/client-unpaid-invoices?client_id=${id}`,
      );
    },
    enabled: !!id,
  });
